import React, { PureComponent } from 'react';
import moment from 'moment';
import Autolinker from 'autolinker';
import { withTranslation, Trans } from 'react-i18next';

import { getFormattedDuration } from 'utils/time-tracking';

import UserName from 'components/user-name';
import UsersList from 'components/users-list';
import TaskGroupName from 'components/task-group-name';
import SelectTaskGroupPopout from 'components/SelectTaskGroupPopout';
import LabelsList from 'components/labels-list';
import TaskCompleteToggle from 'components/TaskCompleteToggle';
import MultiLineInput from 'components/MultiLineInput';
import NiftyMarkdown from 'components/nifty-markdown';
import CanUser from 'components/can-user';
import DateElement from 'components/date-element';
import ProjectName from 'components/project-name';
import DateTime from 'components/DateTime';
import OverflowTooltip from 'components/overflow-tooltip';
import NotificationsBubble from 'components/NotificationsBubble';
import TaskTaskGroup from 'components/TaskTaskGroup';

import IconStopwatch from 'icons/icon-stopwatch';
import IconCalendarSimple from 'icons/icon-calendar-simple';
import IconCheck from 'icons/icon-check';
import IconSubtask from 'icons/icon-subtask';
import IconTransfer from 'icons/icon-transfer';
import IconArrowDown from 'icons/icon-arrow-down';
import IconPlus from 'icons/icon-plus';
import IconProject from 'icons/icon-project';
import IconDiscussionAlt from 'icons/icon-discussion-alt';

class TaskListItem extends PureComponent {
    static defaultProps = {
        name: '',
        assignees: [],
        showComplete: true
    };

    state = {
        focus: false,
        editing: this.props.editing,
        utilActionDropdownVisible: false,
        name: this.props.name
    };

    autolinker = new Autolinker({
        email: false,
        className: 'link-for-preview',
        stripPrefix: false
    });

    beginEdit(force) {
        if (!this.props.canEdit && !force) {
            return;
        }

        this.props.onEditStart && this.props.onEditStart(this.props.id);

        this.setState({
            editing: true
        }, () => {
            if (!this.nameInputCtrl) {
                return;
            }

            this.nameInputCtrl.focus(true);
        });
    }

    endEdit() {
        this.nameInputCtrl && this.nameInputCtrl.blur();
    }

    handleClick = () => {
        this.props.onClick && this.props.onClick(this.props.id);
    }

    handleDoubleClick = () => {
        this.props.onDoubleClick && this.props.onDoubleClick(this.props.id);
    }

    handleToggleCompleteClick = (completed, taskGroupId) => {
        if (this.props.new) {
            this.beginEdit(true);

            return;
        }

        if (completed) {
            this.props.onComplete && this.props.onComplete(this.props.id);
        } else {
            this.props.onUncomplete && this.props.onUncomplete(this.props.id, taskGroupId);
        }
    }

    handleInputBlur = nextName => {
        const nameChanged = !!(this.props.new && nextName !== '') || !!(this.props.name !== nextName);

        if (this.state.editing) {
            this.props.onEditEnd && this.props.onEditEnd(this.props.id);
        }

        this.setState({
            focus: false,
            editing: false
        });

        if (!nameChanged) {
            return;
        }

        if (nextName === '') {
            this.props.onDelete && this.props.onDelete(this.props.id);
        } else {
            this.props.onNameChange && this.props.onNameChange(this.props.id, nextName);
        }
    }

    handleInputFocus = () => {
        this.setState({
            focus: true
        });
    }

    handleChange = name => {
        this.setState({ name });
    }

    handleKeyDown = event => {
        if (event.which === 13 && !event.shiftKey) {
            event.preventDefault();

            if (!this.state.name !== '') {
                this.props.onEnterPress && this.props.onEnterPress(this.props.id);
            }
        }
    }

    handleUtilActionDropdownShow = () => {
        this.setState({
            utilActionDropdownVisible: true
        });
    }

    handleUtilActionDropdownHide = () => {
        this.setState({
            utilActionDropdownVisible: false
        });
    }

    handleTaskGroupSelect = (id, projectId) => {
        this.props.onTaskGroupSelect && this.props.onTaskGroupSelect(this.props.id, id, projectId);
    }

    handleDueDateClick = () => {
        if (this.props.completed) {
            return;
        }

        this.dueDateCtrl && this.dueDateCtrl.show();
    }

    handleDueDateChange = nextDate => {
        this.props.onDueDateChange(this.props.id, nextDate);
    }

    handleDueDateRemove = () => {
        this.props.onDueDateChange(this.props.id, null);
    }

    handleCommentsClick = () => {
        if (!this.props.onCommentsClick) {
            return;
        }

        this.props.onCommentsClick(this.props.id);
    }

    setNameInputCtrl = ctrl => this.nameInputCtrl = ctrl;

    get itemMeta() {
        return (
            <div className="item-meta">
                { this.projectElement }

                { this.niceIdElement }

                { this.labelsElement }

                { this.completedByElement }

                { this.elapsedTimeElement }

                { this.dueDateElement }

                { this.subtasksCountElement }

                { this.commentsElement }
            </div>
        );
    }

    get projectElement() {
        if (!this.props.projectId || !this.props.showProject) {
            return null;
        }

        return (
            <div className="meta-item">
                <div className="meta-item-icon">
                    <IconProject />
                </div>

                <div className="meta-item-body">
                    <ProjectName id={this.props.projectId} />
                </div>
            </div>
        );
    }

    get niceIdElement() {
        if (!this.props.niceId) {
            return null;
        }

        return (
            <div className="meta-item">
                <div className="meta-item-body">
                    { this.props.niceId }
                </div>
            </div>
        );
    }

    get labelsElement() {
        if (!this.props.labels?.length) {
            return null;
        }

        return (
            <div className="meta-item">
                <div className="meta-item-body">
                    <LabelsList labels={this.props.labels} inverted={true} />
                </div>
            </div>
        );
    }

    get completedByElement() {
        if (!this.props.completedBy) {
            return null;
        }

        return (
            <div className="meta-item">
                <div className="meta-item-icon">
                    <IconCheck />
                </div>

                <div className="meta-item-body">
                    <Trans
                        i18nKey="date:Date by"
                        values={{ date: this.props.completedOn }}
                        components={{
                            DateTime: <DateTime format="dateTime" />,
                            UserName: <UserName id={this.props.completedBy} />
                        }}
                    />
                </div>
            </div>
        );
    }

    get elapsedTimeElement() {
        const { t } = this.props;

        if (!this.props.elapsedTime) {
            return null;
        }

        return (
            <div className="meta-item">
                <div className="meta-item-icon">
                    <IconStopwatch />
                </div>

                <div className="meta-item-body">
                    {
                        t('Elapsed: time', {
                            time: getFormattedDuration(this.props.elapsedTime)
                        })
                    }
                </div>
            </div>
        );
    }

    get commentsElement() {
        if (!this.props.comments) {
            return null;
        }

        return (
            <div className={'meta-item' + (this.props.onCommentsClick ? ' item-click-action' : '') + (this.props.hasNotifications ? ' text-caution' : '')} onClick={this.handleCommentsClick}>
                <div className="meta-item-icon">
                    <IconDiscussionAlt />
                </div>

                <div className="meta-item-body">
                    { this.props.comments }
                </div>
            </div>
        );
    }

    get dueDateElement() {
        if (!this.props.dueDate) {
            return null;
        }

        return (
            <div className={'meta-item' + (moment().diff(this.props.dueDate) > 0 && !this.props.completed ? ' text-warning' : '')}>
                <div className="meta-item-icon">
                    <IconCalendarSimple />
                </div>

                <div className="meta-item-body">
                    <DateTime format="dateTime">{this.props.dueDate}</DateTime>
                </div>
            </div>
        );
    }

    get subtasksCountElement() {
        const { t } = this.props;

        if (!this.props.subtasksCount) {
            return null;
        }

        return (
            <div className="meta-item">
                <div className="meta-item-icon">
                    <IconSubtask />
                </div>

                {
                    typeof this.props.completedSubtasksCount === 'undefined' ? (
                        <div className="meta-item-body">
                            {
                                t('subtasks count', {
                                    count: this.props.subtasksCount
                                })
                            }
                        </div>
                    ) : (
                        <div className="meta-item-body">
                            {this.props.completedSubtasksCount}/{this.props.subtasksCount}
                        </div>
                    )
                }

            </div>
        );
    }

    componentDidMount() {
        const ctrl = {
            beginEdit: () => {
                this.beginEdit();
            },
            endEdit: () => {
                this.endEdit();
            },
            hasFocus: () => this.state.focus,
            reset: () => {
                this.nameInputCtrl && this.nameInputCtrl.reset();
            }
        };

        if (this.props.provideController) {
            if ('current' in this.props.provideController) {
                this.props.provideController.current = ctrl;
            } else {
                this.props.provideController && this.props.provideController(ctrl);
            }
        }

        if (this.props.autofocus && this.nameInputCtrl) {
            this.nameInputCtrl.focus();
        }
    }

    componentWillUnmount() {
        if (this.props.provideController) {
            if ('current' in this.props.provideController) {
                this.props.provideController.current = null;
            } else {
                this.props.provideController && this.props.provideController(null);
            }
        }
    }

    render() {
        const className = ['subtask-item'];
        if (this.props.completed) className.push('completed');
        if (this.props.new) className.push('new');
        if (this.props.isDragging) className.push('is-dragging');
        if (this.props.isOver) className.push('is-over');
        if (this.props.canEdit) className.push('can-edit');
        if (this.state.focus) className.push('focus');
        if (this.state.editing) className.push('editing');
        if (this.state.utilActionDropdownVisible) className.push('util-action-dropdown-visible');
        if (!this.state.name) className.push('no-name');

        const { completed, t } = this.props;

        return (
            <div className={className.join(' ')}>
                <div className="item-click-overlay" onClick={this.handleClick} onDoubleClick={this.handleDoubleClick} />

                {
                    this.props.showComplete ? (
                        <div className="item-head">
                            <div className="item-marker item-click-action">
                                {
                                    this.props.new ? (
                                        <IconPlus />
                                    ) : (
                                        <TaskCompleteToggle
                                            locked={this.props.locked}
                                            completed={completed}
                                            projectId={this.props.projectId}
                                            selectListDisabled={this.props.selectListDisabled}
                                            size="medium"
                                            onChange={this.handleToggleCompleteClick}
                                        />
                                    )
                                }
                            </div>
                        </div>
                    ) : null
                }

                <div className="item-body">
                    <div className="item-body-inner">
                        <div className={'item-title-holder' + (this.props.notificationsCount ? ' with-notifications' : '')}>
                            {
                                this.props.richPreview && !this.state.editing ? (
                                    <div className="item-title">
                                        <NiftyMarkdown>{ this.props.name }</NiftyMarkdown>
                                    </div>
                                ) : (
                                    <MultiLineInput
                                        className="item-title"
                                        canEdit={this.state.editing || this.props.name === ''}
                                        autoFocus={!this.props.new}
                                        value={this.props.name}
                                        placeholder={this.props.namePlaceholder}
                                        provideController={this.setNameInputCtrl}
                                        onBlur={this.handleInputBlur}
                                        onFocus={this.handleInputFocus}
                                        onChange={this.handleChange}
                                        onKeyDown={this.handleKeyDown}
                                        onPaste={this.props.onNamePaste}
                                    />
                                )
                            }

                            <NotificationsBubble count={this.props.notificationsCount} />
                        </div>

                        <div className="item-utils">
                            {
                                this.props.onCommentsClick ? (
                                    <div className="util">
                                        <button className="btn btn-square btn-xxxsmall btn-simple btn-icon item-click-action" onClick={this.handleCommentsClick}>
                                            <IconDiscussionAlt size="10px" />
                                        </button>
                                    </div>
                                ) : null
                            }

                            {
                                this.props.onDueDateChange ? (
                                    <CanUser type="editTask" customList={[this.props.createdBy]} key={this.props.id}>
                                        <div className="util">
                                            <button className="btn btn-square btn-xxxsmall btn-simple btn-icon item-click-action" disabled={completed} onClick={this.handleDueDateClick}>
                                                <IconCalendarSimple size="10px" />
                                            </button>

                                            <DateElement
                                                selected={this.props.dueDate}
                                                title={this.props.dueDate ? t('actions.dueDate.titleChange') : t('actions.dueDate.title')}
                                                xAlign="right"
                                                defaultXPosition="left"
                                                removePopoutTitle={ t('actions.dueDate.remove.title') }
                                                removeBodyText={ t('actions.dueDate.remove.description') }
                                                showTimeSelect={true}
                                                showQuickOptions={true}
                                                onChange={this.handleDueDateChange}
                                                onRemove={this.handleDueDateRemove}
                                                provideController={ctrl => this.dueDateCtrl = ctrl}
                                            />
                                        </div>
                                    </CanUser>
                                ) : null
                            }

                            {
                                this.props.assignees.length || this.props.showAssignees ? (
                                    <div className="util">
                                        <div className="item-users">
                                            <UsersList
                                                size="xxsmall"
                                                users={this.props.assignees}
                                                projectId={this.props.projectId}
                                                border={true}
                                                maxUsersCount={3}
                                                {...this.props.usersListProps}
                                            />
                                        </div>
                                    </div>
                                ) : null
                            }

                            {
                                this.props.utilElement ? (
                                    <div className="util">
                                        { this.props.utilElement }
                                    </div>
                                ) : null
                            }
                        </div>
                    </div>

                    { this.itemMeta }
                </div>

                <div className="item-foot">
                    {
                        this.props.taskGroup ? (
                            this.props.onTaskGroupSelect && this.props.projectId ? (
                                <div className="task-group-select item-click-action">
                                    <TaskTaskGroup
                                        id={this.props.id}
                                        icon={<IconTransfer />}
                                        withIcon={true}
                                        onChange={this.handleTaskGroupSelect}
                                        onComplete={this.handleToggleCompleteClick}
                                    />
                                </div>
                            ) : (
                                <div className="task-group-select">
                                    <div className="task-group-select-icon">
                                        <IconTransfer />
                                    </div>

                                    <TaskGroupName id={this.props.taskGroup} />
                                </div>
                            )
                        ) : null
                    }
                </div>
            </div>
        )
    }
}

export default withTranslation(['task', 'date'])(TaskListItem);
